import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import { useTranslation } from 'react-i18next';
import { api, apiPost, refreshApiBaseDetection } from '../lib/api.js';
import SEO from '../components/SEO';

export default function Register() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [role, setRole] = useState('client'); // 'client' | 'creative' | 'both'
  const selectedRoles = role === 'both' ? ['client','creative'] : [role];
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [humanToken, setHumanToken] = useState('');
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  const appearance = import.meta.env.VITE_TURNSTILE_APPEARANCE || 'interaction-only';
  const turnstileSize = import.meta.env.VITE_TURNSTILE_SIZE || 'normal';
  const widgetIdRef = useRef(null);
  const [redirecting, setRedirecting] = useState(false);

  // Theme-aware for Turnstile
  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

  // If already authenticated, skip register and go to dashboard (or intended redirect)
  // Uses useLayoutEffect for synchronous check before paint
  useLayoutEffect(() => {
    try {
      const token = localStorage.getItem('auth:token');
      if (token) {
        setRedirecting(true);
        const params = new URLSearchParams(window.location.search || '');
        const redirect = params.get('redirect');
        navigate(redirect ? decodeURIComponent(redirect) : '/dashboard', { replace: true });
      }
    } catch (e) { void e; }
  }, [navigate]);

  

  // Load and render Cloudflare Turnstile widget
  useEffect(() => {
    if (!siteKey) return;
    const onloadName = 'onTurnstileLoad';
    const ensureRender = () => {
      try {
        const container = document.getElementById('turnstile-register');
        if (!window.turnstile || !container) return;
        if (widgetIdRef.current) {
          try { window.turnstile.reset(widgetIdRef.current, { theme: isDark ? 'dark' : 'light' }); } catch (e) { void e; }
          return;
        }
        const id = window.turnstile.render(container, {
          sitekey: siteKey,
          theme: isDark ? 'dark' : 'light',
          appearance,
          size: turnstileSize,
          callback: (token) => setHumanToken(token),
          'error-callback': () => setHumanToken(''),
          'timeout-callback': () => setHumanToken(''),
        });
        widgetIdRef.current = id;
      } catch (e) { void e; }
    };
    if (window.turnstile) { ensureRender(); return; }
    window[onloadName] = () => ensureRender();
    const existing = document.querySelector('script[data-turnstile]');
    if (!existing) {
      const s = document.createElement('script');
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=' + onloadName;
      s.async = true;
      s.defer = true;
      s.setAttribute('data-turnstile', '1');
      document.head.appendChild(s);
    }
    return () => {
      try { if (window.turnstile && widgetIdRef.current) window.turnstile.remove(widgetIdRef.current); } catch (e) { void e; }
      widgetIdRef.current = null;
    };
  }, [siteKey, isDark, appearance, turnstileSize]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (password !== confirmPassword) {
        throw new Error('Passwords do not match.');
      }
      if (siteKey && !humanToken) {
        throw new Error('Please complete the human verification.');
      }
      const payload = {
        email,
        password,
        confirm_password: confirmPassword,
        first_name: firstName,
        last_name: lastName,
        role,
        turnstileToken: humanToken,
      };
      payload.roles = selectedRoles;
      const res = await apiPost('/users/register', payload);
      const data = res?.data || res;
      const token = data?.token || data?.data?.token;
      const user = data?.user || data?.data?.user;
      if (!token) throw new Error('No token returned');

      try {
        localStorage.setItem('auth:token', token);
        if (api?.defaults) api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      } catch (e) { void e; }

      // Ensure API base matches the environment that validates this token
      try { await refreshApiBaseDetection(); } catch (e) { void e; }

      let me = user;
      if (!me) {
        try {
          const meRes = await api.get('/users/me', { headers: { Authorization: `Bearer ${token}` } });
          const m = meRes?.data || meRes;
          me = m?.data?.user || m?.user || m?.data || null;
        } catch (e) { void e; }
      }
      if (!me) {
        me = { email, display_name: `${firstName} ${lastName}`.trim(), role };
      }
      try { localStorage.setItem('auth:user', JSON.stringify(me)); } catch (e) { void e; }

      if (me && !me.roles) { me.roles = selectedRoles; try { localStorage.setItem('auth:user', JSON.stringify(me)); } catch (e) { void e; } }
      // If both roles are selected, short-circuit to combined dashboard
      if (role === 'both') { navigate('/dashboard?both=1'); return; }
      
      // Navigate to a dashboard (placeholder route) or home
      if (role === 'creative') navigate('/dashboard/creative');
      else navigate('/dashboard/client');
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Registration failed. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (redirecting) return null;
  return (
    <>
      <SEO
        title="Create Account - Join Kreatives | Nordic Creative Platform"
        description="Join Kreatives, the premier Nordic creative talent platform. Create your account to connect with creative opportunities across Sweden, Norway, Denmark, Finland, and Iceland."
        keywords="create account, join kreatives, register, nordic creative platform, creative talent, signup, sweden, norway, denmark, finland, iceland"
        image="/logo-kreativ-light.png"
        type="website"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "Create Account - Kreatives",
          "description": "Join Kreatives, the premier Nordic creative talent platform",
          "mainEntity": {
            "@type": "Organization",
            "name": "Kreatives",
            "description": "Nordic Creative Talent Platform"
          }
        }}
      />
      <div className="min-h-screen w-full grid grid-cols-1 md:grid-cols-10 items-stretch">
      {/* Left hero with centered large logo; consistent with Login */}
      <div className="flex items-center justify-center md:col-span-7 w-full h-full px-8 md:px-12 py-8 bg-transparent">
        <div className="w-full flex items-center justify-center">
          <Logo className="h-72 md:h-80 w-auto" alt="Kreatives" variant="footer" />
        </div>
      </div>

      {/* Right: form aligned right without divider, edge-to-edge */}
      <div className="flex items-center justify-center md:justify-end md:col-span-3">
        <div className="w-full bg-transparent px-4 sm:px-6 md:px-8 py-6 md:py-8 max-w-md mx-auto md:mx-0 md:ml-auto">
          <div className="mb-6">
            <h1 className="text-2xl font-semibold text-primary-text">{t('auth.register.title')}</h1>
            <p className="mt-2 text-sm text-secondary-text">
              {t('auth.register.subtitle')}{' '}
              <Link to="/login" className="text-brand-600 dark:text-brand-400 hover:underline hover:text-brand-700 dark:hover:text-brand-300">
                {t('auth.register.signIn')}
              </Link>
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label htmlFor="firstName" className="block text-sm font-medium text-primary-text mb-1">{t('auth.register.firstName')}</label>
              <div className="relative">
                <svg aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-secondary-text" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5Zm0 2c-4.33 0-8 2.17-8 5v1h16v-1c0-2.83-3.67-5-8-5Z" />
                </svg>
                <input
                  id="firstName"
                  type="text"
                  required
                  className="w-full bg-input-bg border border-card-border rounded-lg pl-10 pr-4 py-3 text-primary-text placeholder-secondary-text focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  placeholder={t('auth.register.firstName')}
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label htmlFor="lastName" className="block text-sm font-medium text-primary-text mb-1">{t('auth.register.lastName')}</label>
              <div className="relative">
                <svg aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-secondary-text" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5Zm0 2c-4.33 0-8 2.17-8 5v1h16v-1c0-2.83-3.67-5-8-5Z" />
                </svg>
                <input
                  id="lastName"
                  type="text"
                  required
                  className="w-full bg-input-bg border border-card-border rounded-lg pl-10 pr-4 py-3 text-primary-text placeholder-secondary-text focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  placeholder={t('auth.register.lastName')}
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-primary-text mb-1">{t('auth.register.email') || t('auth.login.email')}</label>
              <div className="relative">
                <svg aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-secondary-text" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M2 6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2Zm2 0 8 5 8-5H4Zm16 12V8l-8 5-8-5v10h16Z" />
                </svg>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="w-full bg-input-bg border border-card-border rounded-lg pl-10 pr-4 py-3 text-primary-text placeholder-secondary-text focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-primary-text mb-1">{t('auth.register.password') || t('auth.login.password')}</label>
            <div className="relative">
              <svg aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-secondary-text" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2a6 6 0 0 0-6 6v2H5a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-1V8a6 6 0 0 0-6-6Zm-4 8V8a4 4 0 1 1 8 0v2H8Zm-3 2h14v8H5v-8Z" />
              </svg>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                required
                className="w-full bg-input-bg border border-card-border rounded-lg pl-10 pr-10 py-3 text-primary-text placeholder-secondary-text focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-text hover:text-primary-text"
                onClick={() => setShowPassword((v) => !v)}
              >
                {showPassword ? (
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 5c-7 0-10 7-10 7s3 7 10 7 10-7 10-7-3-7-10-7Zm0 12c-2.76 0-5-2.24-5-5 0-.62.12-1.21.33-1.75l6.42 6.42c-.54.21-1.13.33-1.75.33Zm4.67-2.25-6.42-6.42C10.79 8.12 11.38 8 12 8c2.76 0 5 2.24 5 5 0 .62-.12 1.21-.33 1.75Z" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 5c-7 0-10 7-10 7s3 7 10 7 10-7 10-7-3-7-10-7Zm0 12a5 5 0 1 1 0-10 5 5 0 0 1 0 10Z" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-primary-text mb-1">{t('auth.register.confirmPassword')}</label>
            <div className="relative">
              <svg aria-hidden="true" className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-secondary-text" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2a6 6 0 0 0-6 6v2H5a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-1V8a6 6 0 0 0-6-6Zm-4 8V8a4 4 0 1 1 8 0v2H8Zm-3 2h14v8H5v-8Z" />
              </svg>
              <input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                required
                className="w-full bg-input-bg border border-card-border rounded-lg pl-10 pr-10 py-3 text-primary-text placeholder-secondary-text focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
          </div>

            <div>
              <label className="block text-sm font-medium text-primary-text mb-1">{t('auth.register.roleLabel')}</label>
              <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Role">
                <button
                  type="button"
                  className={`w-full px-3 py-2 rounded-lg border flex flex-col items-center gap-1 ${role === 'client' ? 'border-brand-500 bg-brand-50 text-primary-text dark:bg-brand-950' : 'border-card-border text-primary-text'}`}
                  aria-pressed={role === 'client'}
                  onClick={() => setRole('client')}
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7 18c0 1.1.9 2 2 2s2-.9 2-2-.9-2-2-2-2 .9-2 2zM1 2v2h2l3.6 7.59-1.35 2.45c-.16.28-.25.61-.25.96 0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12L8.1 13h7.45c.75 0 1.41-.41 1.75-1.03L21.7 4H5.21l-.94-2H1zm16 16c0 1.1.9 2 2 2s2-.9 2-2-.9-2-2-2-2 .9-2 2z"/>
                  </svg>
                  <span className="text-xs">{t('auth.register.roleClient')}</span>
                </button>
                <button
                  type="button"
                  className={`w-full px-3 py-2 rounded-lg border flex flex-col items-center gap-1 ${role === 'creative' ? 'border-brand-500 bg-brand-50 text-primary-text dark:bg-brand-950' : 'border-card-border text-primary-text'}`}
                  aria-pressed={role === 'creative'}
                  onClick={() => setRole('creative')}
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                  <span className="text-xs">{t('auth.register.roleCreative')}</span>
                </button>
                <button
                  type="button"
                  className={`w-full px-3 py-2 rounded-lg border flex flex-col items-center gap-1 ${role === 'both' ? 'border-brand-500 bg-brand-50 text-primary-text dark:bg-brand-950' : 'border-card-border text-primary-text'}`}
                  aria-pressed={role === 'both'}
                  onClick={() => setRole('both')}
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A1.5 1.5 0 0 0 18.54 7H16.8c-.8 0-1.54.5-1.85 1.26l-1.92 5.76A2 2 0 0 0 15 16.5V22h5zM12.5 11.5c.83 0 1.5-.67 1.5-1.5s-.67-1.5-1.5-1.5S11 9.17 11 10s.67 1.5 1.5 1.5zM5.5 6c1.11 0 2-.89 2-2s-.89-2-2-2-2 .89-2 2 .89 2 2 2zm2 16v-7H9l-1.5-4.5A1.5 1.5 0 0 0 6.08 9H4.92c-.8 0-1.54.5-1.85 1.26L1.5 15H3v7h4.5z"/>
                  </svg>
                  <span className="text-xs">{t('auth.register.roleBoth')}</span>
                </button>
              </div>
            </div>

            {siteKey && (
              <div className="pt-2">
                <div id="turnstile-register" className="inline-block" />
              </div>
            )}

            {error && (
              <div className="text-sm text-red-500" role="alert">
                {error}
              </div>
            )}

            <button type="submit" className="cta-brand w-full py-3" data-variant="primary" disabled={loading || (!!siteKey && !humanToken)}>
              {loading ? '…' : t('auth.register.submit')}
            </button>
          </form>
        </div>
      </div>
      </div>
    </>
  );
}
